import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useApi } from '../../hooks/useApi';
import styles from './courses.module.css';
import { useStores } from '../../hooks/useStore';
import { observer } from 'mobx-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/UI/Button/Button';
import { ReactComponent as EditIco } from './icon/edit.svg';
import { Course, CourseModule, Lesson } from '../../models/courses';
import CoursesPopUP from '../../components/popUp/Courses/Courses';
import CourseModules from './CourseModules';
import { ModuleLessons } from './ModuleLessons';
import LessonEditor from './LessonEditor';
import AssignmentEditor from './AssignmentEditor';
import LessonTabs from './components/LessonTabs';

type DrillLevel =
  | 'COURSE_LIST'
  | 'MODULE_LIST'
  | 'LESSON_LIST'
  | 'LESSON_EDITOR'
  | 'ASSIGNMENT_EDITOR';

interface SortableCourseRowProps {
  course: Course;
  onEdit: (item: Course) => void;
  onSelect: (item: Course) => void;
}

const SortableCourseRow: React.FC<SortableCourseRowProps> = ({
  course,
  onEdit,
  onSelect,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: course.id });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={rowStyle}
      className={styles.courseRow}
      onClick={() => onSelect(course)}
    >
      <div
        className={styles.dragHandle}
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
          <circle cx="5" cy="4" r="1.5" fill="#b4b8cf" />
          <circle cx="11" cy="4" r="1.5" fill="#b4b8cf" />
          <circle cx="5" cy="10" r="1.5" fill="#b4b8cf" />
          <circle cx="11" cy="10" r="1.5" fill="#b4b8cf" />
          <circle cx="5" cy="16" r="1.5" fill="#b4b8cf" />
          <circle cx="11" cy="16" r="1.5" fill="#b4b8cf" />
        </svg>
      </div>

      <img
        className={styles.courseRowImage}
        src={course.image}
        alt={course.title}
      />

      <div className={styles.courseRowInfo}>
        <span className={styles.courseRowTitle}>{course.title}</span>
        <span className={styles.courseRowUrl}>{course.url}</span>
        {course.description && (
          <span className={styles.courseRowDesc}>{course.description}</span>
        )}
      </div>

      <div className={styles.courseRowBadges}>
        <span
          className={
            course.is_published ? styles.badgePublished : styles.badgeDraft
          }
        >
          {course.is_published ? 'Опубликован' : 'Черновик'}
        </span>
      </div>

      <div className={styles.courseRowActions}>
        <EditIco
          className={styles.tagDeleteIco}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(course);
          }}
        />
      </div>
    </div>
  );
};

const Courses: React.FC = observer(() => {
  const api = useApi();
  const store = useStores();
  const navigate = useNavigate();
  const location = useLocation();
  const { courseId, moduleId, lessonId } = useParams();
  const isAssignmentTab = location.pathname.endsWith('/assignment');

  const [showForm, setShowForm] = useState<boolean>(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [course, setCourse] = useState<Course | null>(null);

  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(
    null,
  );
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);

  // Уровень вложенности выводится из URL, а не из локального стейта —
  // при перезагрузке страница остаётся на том же уровне.
  const drillLevel: DrillLevel = lessonId
    ? isAssignmentTab
      ? 'ASSIGNMENT_EDITOR'
      : 'LESSON_EDITOR'
    : moduleId
      ? 'LESSON_LIST'
      : courseId
        ? 'MODULE_LIST'
        : 'COURSE_LIST';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line
  }, []);

  // Резолв курса из URL: ищем в уже загруженном списке.
  useEffect(() => {
    if (!courseId) {
      setSelectedCourse(null);
      return;
    }
    if (selectedCourse?.id === courseId) {
      return;
    }
    const found = courses.find((c) => c.id === courseId);
    if (found) {
      setSelectedCourse(found);
    }
  }, [courseId, courses, selectedCourse]);

  // Резолв модуля из URL: подгружаем модули курса и ищем нужный.
  useEffect(() => {
    if (!courseId || !moduleId) {
      setSelectedModule(null);
      return;
    }
    if (selectedModule?.id === moduleId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { body } = await api.courses.getModules(courseId);
        if (!cancelled && body) {
          const found = body.find((m) => m.id === moduleId);
          if (found) {
            setSelectedModule(found);
          }
        }
      } catch {
        store.notificationsStore.setError('Ошибка при загрузке модуля');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, moduleId, selectedModule, api, store]);

  // Резолв урока из URL: подгружаем уроки модуля и ищем нужный.
  useEffect(() => {
    if (!moduleId || !lessonId) {
      setSelectedLesson(null);
      return;
    }
    if (selectedLesson?.id === lessonId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { body } = await api.courses.getLessons(moduleId);
        if (!cancelled && body) {
          const found = body.find((l) => l.id === lessonId);
          if (found) {
            setSelectedLesson(found);
          }
        }
      } catch {
        store.notificationsStore.setError('Ошибка при загрузке урока');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleId, lessonId, selectedLesson, api, store]);

  const loadCourses = async () => {
    try {
      store.notificationsStore.showPreloader();
      const { body } = await api.courses.get(0, 1000);
      if (body) {
        const sorted = [...(body as Course[])].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        );
        setCourses(sorted);
      }
    } catch {
      store.notificationsStore.setError('Ошибка при загрузке');
    } finally {
      store.notificationsStore.hidePreloader();
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = courses.findIndex((c) => c.id === active.id);
    const newIndex = courses.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(courses, oldIndex, newIndex);
    setCourses(reordered);

    try {
      const updates = reordered
        .map((c, index) => ({ course: c, newOrder: index }))
        .filter(({ course: c, newOrder }) => (c.sort_order ?? 0) !== newOrder);

      await Promise.all(
        updates.map(({ course: c, newOrder }) => {
          const fd = new FormData();
          fd.append('title', c.title);
          fd.append('url', c.url);
          if (c.description) {
            fd.append('description', c.description);
          }
          fd.append('sort_order', String(newOrder));
          fd.append('is_published', String(c.is_published ?? false));
          return api.courses.update(fd, c.id);
        }),
      );

      setCourses((prev) =>
        prev.map((c, index) => ({ ...c, sort_order: index })),
      );
    } catch {
      store.notificationsStore.setError('Ошибка при сохранении порядка');
      loadCourses();
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setCourse(null);
  };

  const update = () => {
    loadCourses();
  };

  const editItem = (item: Course) => {
    setCourse(item);
    setShowForm(true);
  };

  const handleSelectCourse = (item: Course) => {
    setSelectedCourse(item);
    navigate(`/courses/${item.id}`);
  };

  const handleSelectModule = (module: CourseModule) => {
    setSelectedModule(module);
    navigate(`/courses/${courseId}/${module.id}`);
  };

  const handleSelectLesson = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    navigate(`/courses/${courseId}/${moduleId}/${lesson.id}`);
  };

  const handleBackToCourselist = () => {
    navigate('/courses');
  };

  const handleBackToModules = () => {
    navigate(`/courses/${courseId}`);
  };

  const handleBackToLessons = () => {
    navigate(`/courses/${courseId}/${moduleId}`);
  };

  return (
    <>
      {showForm && (
        <CoursesPopUP onClose={closeForm} update={update} item={course} />
      )}
      <div className={styles.container}>
        {drillLevel === 'COURSE_LIST' && (
          <div className={styles.pageName}>
            <div className={styles.top}>
              <div className={styles.title}>
                <h2>Курсы</h2>
              </div>
              <Button
                style={{ maxWidth: 150 }}
                appearance="auth"
                onClick={() => setShowForm(showForm ? false : true)}
              >
                Добавить
              </Button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={courses.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={styles.courseList}>
                  {courses.map((row) => (
                    <SortableCourseRow
                      key={row.id}
                      course={row}
                      onEdit={editItem}
                      onSelect={handleSelectCourse}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {drillLevel === 'MODULE_LIST' && selectedCourse && (
          <CourseModules
            course={selectedCourse}
            onSelectModule={handleSelectModule}
            onBack={handleBackToCourselist}
          />
        )}

        {drillLevel === 'LESSON_LIST' && selectedCourse && selectedModule && (
          <ModuleLessons
            course={selectedCourse}
            module={selectedModule}
            onSelectLesson={handleSelectLesson}
            onBack={handleBackToModules}
            onBackToCourses={handleBackToCourselist}
          />
        )}

        {(drillLevel === 'LESSON_EDITOR' ||
          drillLevel === 'ASSIGNMENT_EDITOR') &&
          selectedCourse &&
          selectedModule &&
          selectedLesson && (
            <div>
              <LessonTabs
                active={isAssignmentTab ? 'assignment' : 'content'}
                onChange={(tab) =>
                  navigate(
                    tab === 'assignment'
                      ? `/courses/${courseId}/${moduleId}/${lessonId}/assignment`
                      : `/courses/${courseId}/${moduleId}/${lessonId}`,
                  )
                }
              />
              {drillLevel === 'LESSON_EDITOR' ? (
                <LessonEditor
                  key={selectedLesson.id}
                  course={selectedCourse}
                  module={selectedModule}
                  lesson={selectedLesson}
                  onBack={handleBackToLessons}
                  onSaved={handleBackToLessons}
                  onBackToCourses={handleBackToCourselist}
                  onBackToModules={handleBackToModules}
                />
              ) : (
                <AssignmentEditor
                  key={selectedLesson.id}
                  course={selectedCourse}
                  module={selectedModule}
                  lesson={selectedLesson}
                  onBack={handleBackToLessons}
                  onBackToCourses={handleBackToCourselist}
                  onBackToModules={handleBackToModules}
                />
              )}
            </div>
          )}

        {drillLevel !== 'COURSE_LIST' &&
          !(
            (drillLevel === 'MODULE_LIST' && selectedCourse) ||
            (drillLevel === 'LESSON_LIST' &&
              selectedCourse &&
              selectedModule) ||
            ((drillLevel === 'LESSON_EDITOR' ||
              drillLevel === 'ASSIGNMENT_EDITOR') &&
              selectedCourse &&
              selectedModule &&
              selectedLesson)
          ) && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: 40,
              }}
            >
              <CircularProgress />
            </div>
          )}
      </div>
    </>
  );
});

export default Courses;
